import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { FileDoc, FilePageDoc, SpecSection } from 'common';
import { FileService } from 'src/global/service/FileService';

@Component({
  tag: 'document-cache-modal',
})
export class DocumentCacheModal {
  static async present(projectId: string, file: FileDoc): Promise<SpecSection | null> {
    return new Promise(async resolve => {
      const modal = await modalController.create({
        component: 'document-cache-modal',
        componentProps: { projectId, file },
        cssClass: ['concourse-modal', 'modal-height-auto'],
        backdropDismiss: false,
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  @Prop() projectId: string;
  @Prop() file: FileDoc;
  @Event() dismiss: EventEmitter<void>;

  @State() pages: FilePageDoc[] = [];
  @State() cached: string[] = [];
  @State() caching: boolean = false;

  async componentDidLoad() {
    const db = FileService.getFilePagesDb(this.projectId, this.file._id);
    this.pages = await db.getDocs();
    this.cached = await this.getCachedPages(this.pages);
  }

  async getCachedPages(pages: FilePageDoc[]): Promise<string[]> {
    const cacheChecked = await Promise.all(pages.map(page => this.isCached(page)));
    return pages.filter((_, idx) => cacheChecked[idx]).map(p => p._id);
  }

  async isCached(page: FilePageDoc): Promise<boolean> {
    return !!(await FileService.getCacheValue(FileService.getSVGKey(this.projectId, page.file_id, page._id)));
  }

  async startCaching() {
    this.caching = true;
    this.cacheNextPage();
  }

  async cacheNextPage() {
    const next = this.pages.find(page => !this.cached.includes(page._id));
    if (next) {
      await FileService.loadSvgToString(this.projectId, next);
      this.cached = [...this.cached, next._id];
      this.cacheNextPage();
    } else {
      this.caching = false;
    }
  }
  private async downloadFile() {
    const url = await FileService.getSignedUrl(this.file.s3_key);
    window.open(url, '_blank');
  }

  render() {
    const descriptor = this.cached.length < this.pages.length ? `${this.cached.length} pages out of ${this.pages.length}` : 'All pages';
    return (
      <div style={{ width: '340px' }}>
        <ion-toolbar>
          <ion-title>Document Cache</ion-title>
          <ion-button slot="end" fill="clear" onClick={() => this.dismiss.emit()}>
            <ion-icon slot="icon-only" name="close"></ion-icon>
          </ion-button>
        </ion-toolbar>
        <div class="ion-padding">
          {this.caching && <ion-spinner name="crescent" />}
          {descriptor} in this document have been cached.
        </div>
        <ion-toolbar>
          <ion-button slot="start" fill="clear" onClick={() => this.downloadFile()}>
            <ion-icon slot="icon-only" name="cloud-download-outline"></ion-icon>
          </ion-button>
          <ion-button slot="end" onClick={() => this.startCaching()} disabled={this.caching || this.cached.length === this.pages.length}>
            Cache {this.pages.length - this.cached.length} pages
          </ion-button>
        </ion-toolbar>
      </div>
    );
  }
}
